import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from '../utils/firebaseConfig';
import './ProductFilters.css';

// Cache para evitar recargar categorías
const CACHE_KEY = 'mi_ecommerce_categories_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos en milisegundos

const getFromCache = () => {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (!stored) return null;
    
    const { data, timestamp } = JSON.parse(stored);
    if (Date.now() - timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error leyendo cache:', error);
    return null;
  }
};

const saveToCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error guardando cache:', error);
  }
};

const ProductFilters = ({ onFilterSelect }) => {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasLoadedCategories, setHasLoadedCategories] = useState(false);
  const menuRef = useRef(null);

  // 🔹 Cargar categorías y marcas SOLO cuando se abre el menú (PRIMERA VEZ)
  useEffect(() => {
    const loadCategoriesAndBrands = async () => {
      try {
        // Si ya cargó, no hacer nada
        if (hasLoadedCategories && categories.length > 0) {
          return;
        }

        // Verificar cache localStorage primero
        const cached = getFromCache();
        if (cached) {
          setCategories(cached);
          setHasLoadedCategories(true);
          setLoading(false);
          return;
        }

        setLoading(true);

        // Cargar todas las categorías con sus marcas asociadas
        const q = query(
          collection(db, 'categories'),
          orderBy('order', 'asc')
        );
        
        const querySnapshot = await getDocs(q);
        const loadedCategories = [];

        // Optimización: usar Promise.all para cargar marcas en paralelo
        const categoryPromises = querySnapshot.docs.map(async (categoryDoc) => {
          const categoryData = categoryDoc.data();
          
          // Cargar marcas de esta categoría
          const brandsRef = collection(db, 'categories', categoryDoc.id, 'brands');
          const brandsSnapshot = await getDocs(brandsRef);
          
          const brands = brandsSnapshot.docs.map(brandDoc => ({
            id: brandDoc.id,
            ...brandDoc.data()
          }));

          return {
            id: categoryDoc.id,
            name: categoryData.name,
            image: categoryData.image || null,
            brands: brands
          };
        });

        const results = await Promise.all(categoryPromises);
        loadedCategories.push(...results);

        // Guardar en localStorage
        saveToCache(loadedCategories);
        setCategories(loadedCategories);
        setHasLoadedCategories(true);
      } catch (error) {
        console.error("❌ Error cargando categorías:", error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    // Solo cargar si el menú está abierto Y no ha cargado todavía
    if (isMenuOpen && !hasLoadedCategories) {
      loadCategoriesAndBrands();
    }
  }, [isMenuOpen, hasLoadedCategories]);

  // 🔹 Emitir filtros SOLO cuando ambos están seleccionados (categoría Y marca)
  // O cuando hay búsqueda de texto
  useEffect(() => {
    // Solo emitir si:
    // 1. Hay categoría Y marca seleccionadas
    // 2. O hay búsqueda de texto
    if ((activeCategory && selectedBrand) || searchTerm) {
      onFilterSelect({ 
        category: activeCategory, 
        brands: selectedBrand ? [selectedBrand] : [], 
        searchTerm 
      });
    } 
    // ⚠️ NO emitir nada si solo hay categoría sin marca
    // Esto evita recargas innecesarias
  }, [activeCategory, selectedBrand, searchTerm, onFilterSelect]);

  // 🔹 Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCategoryClick = (categoryName) => {
    setActiveCategory(activeCategory === categoryName ? null : categoryName);
    setSelectedBrand(null);
  };

  const handleBrandToggle = (brandName) => {
    setSelectedBrand(selectedBrand === brandName ? null : brandName);
    setIsMenuOpen(false);
  };

  const handleReset = () => {
    setActiveCategory(null);
    setSelectedBrand(null);
    setSearchTerm('');
  };

  // 🔹 Obtener marcas de la categoría activa
  const activeCategoryData = categories.find(cat => cat.name === activeCategory);
  const activeBrands = activeCategoryData ? activeCategoryData.brands : [];

  return (
    <div className="product-filters" ref={menuRef}>
      <div className="filters-header">
        <button className="filters-toggle" onClick={toggleMenu}>
          🔍 Filtros {selectedBrand && '(1)'} {loading && isMenuOpen && '⏳'}
        </button>
        <input
          type="text"
          placeholder="Buscar productos por nombre o descripción..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-products-input"
        />
        {(activeCategory || selectedBrand || searchTerm) && (
          <button className="reset-filters" onClick={handleReset}>
            Limpiar ✕
          </button>
        )}
      </div>

      {isMenuOpen && (
        <div className="filters-panel">
          {loading && hasLoadedCategories === false ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>⏳ Cargando categorías...</p>
            </div>
          ) : (
            <>
              {/* Si no hay categoría seleccionada, mostrar todas */}
              {!activeCategory && (
                <div className="categories-list">
                  <h3>Categorías</h3>
                  <div className="categories-grid">
                    {categories.map((category) => (
                      <div key={category.id} className="category-item">
                        <button
                          className={`category-button ${
                            activeCategory === category.name ? 'active' : ''
                          }`}
                          onClick={() => handleCategoryClick(category.name)}
                        >
                          {category.image && (
                            <img src={category.image} alt={category.name} className="category-image" />
                          )}
                          <span>{category.name}</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Si hay categoría seleccionada, mostrar solo esa y sus marcas */}
              {activeCategory && (
                <>
                  <div className="selected-category-header">
                    <button className="back-btn" onClick={() => handleCategoryClick(activeCategory)}>
                      ← Volver a categorías
                    </button>
                    <h3>{activeCategory}</h3>
                  </div>

                  <div className="brands-list">
                    <h3>Marcas</h3>
                    {activeBrands.length > 0 ? (
                      <div className="brands-grid">
                        {activeBrands.map((brand) => (
                          <label key={brand.id} className="brand-item">
                            <input
                              type="radio"
                              name="brand-select"
                              checked={selectedBrand === brand.name}
                              onChange={() => handleBrandToggle(brand.name)}
                              className="brand-checkbox"
                            />
                            {brand.image && (
                              <img src={brand.image} alt={brand.name} className="brand-image" />
                            )}
                            <span>{brand.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="no-brands">No hay marcas disponibles</p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Mostrar filtros activos */}
      {(activeCategory || selectedBrand) && (
        <div className="active-filters">
          {activeCategory && (
            <span className="filter-tag filter-tag-category">
              {activeCategory}
              <button onClick={() => handleCategoryClick(activeCategory)}>✕</button>
            </span>
          )}
          {selectedBrand && (
            <span className="filter-tag filter-tag-brand">
              {selectedBrand}
              <button onClick={() => setSelectedBrand(null)}>✕</button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductFilters;